// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/SiteComposer.tsx
import { useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { ArrowUp } from 'lucide-react';

import { IconButton } from '@/components/ui/icon-button';
import { cn } from '@/lib/utils';

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with the trimmed text on Enter or the send button. Not called when blank or disabled. */
  onSubmit: (value: string) => void;
  placeholder?: string;
  /** The field's accessible name; a placeholder is not a label. Defaults to "Message". */
  label?: string;
  /** A request is in flight: the field and the send button are off and the form is `aria-busy`. */
  disabled?: boolean;
  /** The field's character limit (UTF-16 units, as the BFF counts them). */
  maxLength?: number;
  /**
   * Whether `value` may be sent with the button. Defaults to "not blank". Enter still submits any non-blank
   * text, so an answer that is not sendable is replied to in the chat rather than ignored.
   */
  isSendable?: (value: string) => boolean;
  className?: string;
}

const NOT_BLANK = (value: string): boolean => value.trim().length > 0;

/**
 * TP's SiteComposer: a `rounded-lg` card holding the field and, under it on the right, an arrow button
 * (`IconButton ArrowUp md`). The field takes focus on mount, and again when a request that had switched it off
 * ends. The button waits for `isSendable`; Enter is handled on the field itself, because a form whose submit
 * button is disabled swallows Enter and an invalid address would get no answer (TP is silent there).
 */
export function ChatComposer({
  value,
  onChange,
  onSubmit,
  placeholder,
  label = 'Message',
  disabled = false,
  maxLength,
  isSendable = NOT_BLANK,
  className,
}: ChatComposerProps) {
  const ref = useRef<HTMLInputElement>(null);
  const canSend = !disabled && isSendable(value);

  useEffect(() => {
    if (!disabled) ref.current?.focus();
  }, [disabled]);

  const submit = () => {
    if (!disabled && NOT_BLANK(value)) onSubmit(value.trim());
  };
  const onFormSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Not while an IME composition is being confirmed with Enter.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <form
      onSubmit={onFormSubmit}
      aria-busy={disabled || undefined}
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-ds-hairline bg-card p-3 focus-within:border-ds-focus',
        className,
      )}
    >
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        aria-label={label}
        autoComplete="url"
        spellCheck={false}
        className="min-w-0 bg-transparent px-1 text-body text-foreground outline-none placeholder:text-ds-text-3"
      />
      <div className="flex justify-end">
        <IconButton type="submit" label="Send message" icon={ArrowUp} size="md" disabled={!canSend} aria-busy={disabled || undefined} />
      </div>
    </form>
  );
}
